import {
  ArrowRightIcon,
  CheckCircleIcon,
  CheckIcon,
  CircleNotchIcon,
  FileCodeIcon,
  GitBranchIcon,
  MagnifyingGlassIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import classNames from 'classnames'
import _ from 'lodash'
import { Fragment } from 'react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { useNow } from '../../../../hooks/use-now.ts'
import { Routes } from '../../../../routes.ts'
import { formatElapsed, msToIso } from '../../../../utils/date-utils.ts'
import { pluralize } from '../../../../utils/text-utils.ts'
import { describeReason, groupUnassociatedByReason } from '../../freeform-unassociated-reasons.ts'
import { MissGroups, StatCell } from '../freeform-run-parts.tsx'
import * as sharedStyles from '../freeform-run-styles.css.ts'
import {
  findingLabelOf,
  focusOf,
  type FreeformRunState,
  hasProgress,
  progressPercentOf,
  repositoriesOf,
  repositoryShortName,
  type RunFinding,
  type RunLocation,
  type RunStage,
  stagesOf,
  verifiedLocationsOf,
  whereOf,
} from '../freeform-run.ts'
import * as styles from './freeform-live-view.css.ts'

/** The newest log entries kept on screen; older ones are a scroll nobody makes. */
const VISIBLE_LOG_ENTRIES = 60

/** How long the run has gone on, up to `endIso`. */
const elapsedSince = (startIso: string | undefined, endIso: string | undefined) =>
  startIso && endIso ? formatElapsed(startIso, endIso) : undefined

const phaseCopy = (run: FreeformRunState) =>
  match(run.phase)
    .with('parsing', () => 'Reading your files…')
    .with('associating', () => 'Placing each finding in your repositories…')
    .with('scanning', () => 'Creating a scan for each repository…')
    .with('completed', () => 'Done.')
    .with('failed', () => 'The run stopped.')
    .exhaustive()

export type FreeformLiveViewProps = {
  run: FreeformRunState
  /** Log entries up to this seq were replayed on load and appear without the arrival animation. */
  settledSeq: number
}

/** The live view, with its clock ticking once a second until the run ends. */
export const FreeformLiveView: React.FC<FreeformLiveViewProps> = ({ run, settledSeq }) => {
  const now = useNow({ isTicking: !run.isDone })
  return <FreeformLiveViewPure run={run} settledSeq={settledSeq} nowIso={msToIso(now)} />
}

export type FreeformLiveViewPureProps = FreeformLiveViewProps & {
  /** The wall clock the elapsed time is measured to while the run goes on. */
  nowIso: string
}

/**
 * The live view of a Findings Intake run, drawn entirely from the run state its stream folds into: how far
 * it is, each finding the report holds and where it is landing, the code being verified, and the
 * repositories that will get a scan.
 */
export const FreeformLiveViewPure: React.FC<FreeformLiveViewPureProps> = ({ run, settledSeq, nowIso }) => {
  const latest = run.log.at(-1)
  const percent = progressPercentOf(run)
  const elapsed = elapsedSince(run.startTs, run.isDone ? run.ts : nowIso)

  return (
    <div className={sharedStyles.view}>
      <section className={styles.band} aria-label="Run progress">
        <div className={styles.bandTop}>
          <div>
            <StatusPill run={run} />
            <h2 className={styles.headline}>Turning your report into scans</h2>
            <p
              key={latest?.seq}
              className={classNames(styles.activity, latest && latest.seq > settledSeq && styles.fresh)}
              aria-live="polite"
            >
              {latest?.message ?? phaseCopy(run)}
            </p>
          </div>
          <div className={styles.percentBlock}>
            <span className={styles.percent}>{percent}%</span>
            {elapsed && <span className={styles.elapsed}>{elapsed} elapsed</span>}
          </div>
        </div>
        <div
          className={styles.track}
          role="progressbar"
          aria-label="Findings placed"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className={styles.fill} style={{ width: `${percent}%` }} />
          {!run.isDone && <div className={styles.sweepBar} />}
        </div>
        <StageRail stages={stagesOf(run)} />
        {run.errorReason !== undefined && (
          <p className={styles.failure} role="alert">
            The run stopped before it finished: {run.errorReason}
          </p>
        )}
        {!hasProgress(run) && run.phase !== 'parsing' && (
          <p className={styles.emptyText}>
            Live detail isn&apos;t available for this run. This page updates when it finishes.
          </p>
        )}
      </section>
      <div className={styles.main}>
        <FindingsPanel run={run} />
        <div className={styles.column}>
          <CodePanel run={run} />
          <ActivityLog run={run} settledSeq={settledSeq} />
        </div>
        <div className={styles.column}>
          <RepositoriesPanel run={run} />
          <NeedsAHumanLook run={run} />
          <RunStats run={run} />
        </div>
      </div>
    </div>
  )
}

/** Live while the run goes on; once its stream ends, the view holds still and says how it ended. */
const StatusPill: React.FC<{ run: FreeformRunState }> = ({ run }) =>
  match(run)
    .with({ isDone: false }, () => (
      <span className={styles.statusPill({ status: 'live' })}>
        <span className={styles.liveDot} aria-hidden />
        Live
      </span>
    ))
    .when(
      ({ errorReason }) => errorReason !== undefined,
      () => (
        <span className={styles.statusPill({ status: 'failed' })}>
          <WarningCircleIcon size={12} weight="fill" aria-hidden />
          Failed
        </span>
      )
    )
    .otherwise(() => (
      <span className={styles.statusPill({ status: 'complete' })}>
        <CheckCircleIcon size={12} weight="fill" aria-hidden />
        Complete
      </span>
    ))

const StageRail: React.FC<{ stages: RunStage[] }> = ({ stages }) => (
  <ol className={styles.stages} aria-label="Run stages">
    {stages.map((stage, index) => (
      <li
        key={stage.key}
        className={styles.stage({ state: stage.state })}
        aria-label={`${stage.label}: ${stage.state}`}
      >
        <span className={styles.stageIcon({ state: stage.state })} aria-hidden>
          {match(stage.state)
            .with('done', () => <CheckIcon size={14} weight="bold" />)
            .with('active', () => <CircleNotchIcon size={16} className={styles.spinner} />)
            .with('pending', () => index + 1)
            .exhaustive()}
        </span>
        <span className={styles.stageText}>
          <span className={styles.stageLabel}>{stage.label}</span>
          <span className={styles.stageCount}>{stage.count}</span>
        </span>
      </li>
    ))}
  </ol>
)

const firstVerified = (finding: RunFinding) => finding.locations.find(location => location.state === 'verified')
const firstFound = (finding: RunFinding) => finding.locations.find(location => location.state === 'found')

const chipLabelOf = (finding: RunFinding) =>
  match(finding.state)
    .with('extracted', () => 'Extracted')
    .with('searching', () => (finding.clue ? `Searching ${finding.clue}` : 'Searching'))
    .with('matched', () => `Matched · ${repositoryShortName(finding.repository ?? '')}`)
    .with('located', () => {
      const found = firstFound(finding)
      return found ? `Verifying ${whereOf(found)}` : 'Verifying'
    })
    .with('verified', () => {
      const verified = firstVerified(finding)
      return verified ? whereOf(verified) : 'Verified'
    })
    .with('rejected', () => 'Location rejected')
    .with('unassociated', () => describeReason(finding.reason ?? '').label)
    .exhaustive()

const ChipIcon: React.FC<{ finding: RunFinding }> = ({ finding }) =>
  match(finding.state)
    .with('searching', 'located', () => <CircleNotchIcon size={11} className={styles.spinner} aria-hidden />)
    .with('verified', () => <CheckCircleIcon size={11} weight="fill" aria-hidden />)
    .with('unassociated', () => <WarningCircleIcon size={11} weight="fill" aria-hidden />)
    .otherwise(() => null)

const FindingsPanel: React.FC<{ run: FreeformRunState }> = ({ run }) => (
  <section className={styles.panel} aria-label="Findings">
    <div className={styles.panelHead}>
      <h3 className={styles.panelTitle}>Findings</h3>
      <span className={styles.panelMeta}>
        {run.total !== undefined ? `${run.findings.length} of ${run.total} from the report` : 'Reading the report'}
      </span>
    </div>
    {run.findings.length === 0 ? (
      <div className={styles.empty}>
        <div className={styles.skeleton} />
        <div className={styles.skeleton} />
        <div className={styles.skeleton} />
        <p className={styles.emptyText}>Reading the report to find each item it lists…</p>
      </div>
    ) : (
      <ul className={styles.findings}>
        {run.findings.map(finding => (
          <li
            key={finding.number}
            className={styles.finding({ isFocused: run.focus?.number === finding.number })}
            aria-label={`${finding.sourceId ?? `Finding ${finding.number}`}: ${chipLabelOf(finding)}`}
          >
            <span className={styles.findingId}>{findingLabelOf(finding)}</span>
            <span className={styles.findingTitle} title={finding.title}>
              {finding.title}
            </span>
            {finding.asset && <span className={styles.findingAsset}>{finding.asset}</span>}
            <span className={styles.chip({ state: finding.state })}>
              <ChipIcon finding={finding} />
              <span className={styles.chipText}>{chipLabelOf(finding)}</span>
            </span>
          </li>
        ))}
      </ul>
    )}
  </section>
)

const verdictLabel = (location: RunLocation) =>
  match(location)
    .with({ state: 'found' }, () => 'Verifying')
    .with({ state: 'rejected' }, () => 'Rejected')
    .with({ state: 'verified', note: 'cited' }, () => 'Cited by the report')
    .with({ state: 'verified', note: 'unchecked' }, () => 'Kept unchecked')
    .otherwise(() => 'Verified')

const CodePanel: React.FC<{ run: FreeformRunState }> = ({ run }) => {
  const focused = focusOf(run)
  const searching = run.findings.find(finding => finding.state === 'searching' && finding.clue)
  if (!focused) {
    return (
      <section className={styles.panel} aria-label="Code being examined">
        <div className={styles.codeBar}>
          <GitBranchIcon size={14} aria-hidden />
          <span>Waiting for a match</span>
        </div>
        <div className={styles.searchLine}>
          <MagnifyingGlassIcon size={13} aria-hidden />
          <span>
            {searching?.clue
              ? `Searching ${searching.clue}`
              : 'Reading the report for files, routes and names to search'}
          </span>
        </div>
        <div className={styles.empty}>
          <div className={styles.skeleton} />
          <div className={styles.skeleton} />
          <p className={styles.emptyText}>
            The first located finding appears here, at the line Pixee will triage and fix.
          </p>
        </div>
      </section>
    )
  }

  const { finding, location } = focused
  const lines = location.excerpt?.lines ?? []
  const startLine = location.excerpt?.start_line ?? 1
  // A file-level location has no line to point at, so the callout sits under the top of the file.
  const calloutAfter = location.line ?? startLine
  return (
    <section className={styles.panel} aria-label="Code being examined">
      <div className={styles.codeBar}>
        <GitBranchIcon size={14} aria-hidden />
        <span className={styles.codeRepo}>{location.repository}</span>
        <FileCodeIcon size={14} aria-hidden />
        <span className={styles.codePath} title={location.path}>
          {location.line ? `${location.path}:${location.line}` : location.path}
        </span>
      </div>
      {finding.clue && (
        <div className={styles.searchLine}>
          <MagnifyingGlassIcon size={13} aria-hidden />
          <span>Searched {finding.clue}</span>
        </div>
      )}
      {lines.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.skeleton} />
          <div className={styles.skeleton} />
          <p className={styles.emptyText}>Reading the code at {whereOf(location)}…</p>
        </div>
      ) : (
        <div className={styles.code}>
          {lines.map((text, index) => {
            const lineNumber = startLine + index
            return (
              <Fragment key={lineNumber}>
                <div className={styles.codeLine({ isHighlighted: lineNumber === location.line })}>
                  <span className={styles.lineNumber}>{lineNumber}</span>
                  <span>{text || ' '}</span>
                </div>
                {lineNumber === calloutAfter && (
                  <div key={`${finding.number}-${location.state}`} className={styles.callout}>
                    <div className={styles.calloutHead}>
                      <span className={styles.findingId}>{findingLabelOf(finding)}</span>
                      <span>{finding.title}</span>
                      <span className={styles.verdict({ state: location.state })}>
                        {location.state === 'found' && (
                          <CircleNotchIcon size={11} className={styles.spinner} aria-hidden />
                        )}
                        {location.state === 'verified' && <CheckIcon size={11} weight="bold" aria-hidden />}
                        {verdictLabel(location)}
                      </span>
                    </div>
                    {location.reason && <span className={styles.calloutWhy}>{location.reason}</span>}
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>
      )}
    </section>
  )
}

const ActivityLog: React.FC<{ run: FreeformRunState; settledSeq: number }> = ({ run, settledSeq }) => {
  const shown = run.log.slice(-VISIBLE_LOG_ENTRIES).reverse()
  return (
    <section className={styles.panel} aria-label="Activity">
      <div className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Activity</h3>
        <span className={styles.panelMeta}>newest first</span>
      </div>
      {shown.length === 0 ? (
        <p className={styles.empty}>
          <span className={styles.emptyText}>{phaseCopy(run)}</span>
        </p>
      ) : (
        <ol className={styles.log} role="log" aria-label="Run activity, newest first">
          {shown.map(entry => (
            <li key={entry.seq} className={entry.seq > settledSeq ? styles.fresh : undefined}>
              <div className={styles.logItem}>
                <span className={styles.logTime}>{elapsedSince(run.startTs, entry.ts)}</span>
                <i className={styles.logMark({ tone: entry.tone })} aria-hidden />
                <span className={styles.logMessage}>{entry.message}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

const repositoryNote = ({ verified, pending, total }: { verified: number; pending: number; total: number }) =>
  match({ verified, pending, total })
    .when(
      ({ pending }) => pending > 0,
      () => `${pending} being confirmed`
    )
    .when(
      ({ verified, total }) => verified === total,
      () => `Scan ready · ${verified} ${pluralize(verified, 'finding', 'findings')}`
    )
    .when(
      ({ verified }) => verified > 0,
      () => `${verified} confirmed so far`
    )
    .otherwise(() => 'Waiting for matches')

const RepositoriesPanel: React.FC<{ run: FreeformRunState }> = ({ run }) => {
  const repositories = _.sortBy(repositoriesOf(run), repository => repository.name)
  return (
    <section className={styles.panel} aria-label="Repositories">
      <div className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Repositories</h3>
        <span className={styles.panelMeta}>each gets its own scan</span>
      </div>
      {repositories.length === 0 ? (
        <p className={styles.empty}>
          <span className={styles.emptyText}>Repositories appear as findings are matched to them.</span>
        </p>
      ) : (
        <ul className={styles.repos}>
          {repositories.map(repository => {
            // The platform names each scan as it creates it, so the link appears once the scan exists.
            const scan = run.repoScans.find(candidate => candidate.repoFullName === repository.name)
            return (
              <li
                key={repository.name}
                className={styles.repo({ isBusy: repository.pending > 0 })}
                aria-label={repository.name}
              >
                <div className={styles.repoHead}>
                  <GitBranchIcon size={14} aria-hidden />
                  <span className={styles.repoName} title={repository.name}>
                    {repository.name}
                  </span>
                  <span className={styles.repoCount}>{repository.verified}</span>
                </div>
                <div className={styles.repoTrack}>
                  <div
                    className={styles.repoFill}
                    style={{ width: `${Math.round((repository.verified / repository.findings.length) * 100)}%` }}
                  />
                </div>
                <div className={styles.repoFoot}>
                  <span className={styles.repoNote}>
                    {repositoryNote({ ...repository, total: repository.findings.length })}
                  </span>
                  {scan && (
                    <Link className={styles.scanLink} to={Routes.ScanDetailsPage.createPath({ scanId: scan.scanId })}>
                      View scan
                      <ArrowRightIcon size={12} weight="bold" aria-hidden />
                    </Link>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

/** The findings no repository could take, as they are ruled on: not judged safe, so a person should look. */
const NeedsAHumanLook: React.FC<{ run: FreeformRunState }> = ({ run }) => {
  const misses = run.findings.filter(finding => finding.state === 'unassociated')
  if (misses.length === 0) {
    return null
  }
  const groups = groupUnassociatedByReason(
    misses.map(finding => ({ ...finding, reason: finding.reason ?? '', rationale: finding.rationale ?? '' }))
  )
  return (
    <section className={styles.panel} aria-label="Needs a human look">
      <div className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Needs a human look</h3>
        <span className={styles.panelMeta}>not judged safe</span>
      </div>
      <MissGroups
        className={styles.misses}
        groups={groups.map(group => ({
          ...group,
          findings: group.findings.map(finding => ({
            key: String(finding.number),
            label: findingLabelOf(finding),
            title: finding.title,
            rationale: finding.rationale,
          })),
        }))}
      />
    </section>
  )
}

const RunStats: React.FC<{ run: FreeformRunState }> = ({ run }) => {
  const cells: readonly [number, string][] = [
    [run.searches, 'code searches'],
    [run.copiesIgnored, 'test and fixture copies ignored'],
    [verifiedLocationsOf(run).length, 'locations verified'],
    [run.findings.filter(finding => finding.state === 'unassociated').length, 'need a human look'],
  ]
  return (
    <section className={styles.stats} aria-label="Run totals">
      {cells.map(([value, label]) => (
        <StatCell key={label} value={value} label={label} className={styles.stat} />
      ))}
    </section>
  )
}
